import { WHelper } from '@alice-health/wonderland-react'
import { FunctionComponent, useContext } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { meCompanyFormSchema } from '@/modules/simulator/schemas/MECompanyFormSchema'
import { sp } from '@/modules/simulator/constants/city-options'
import { lgpdConsentText } from '@/modules/simulator/constants/lgpd'
import {
  useAnswerSimulation,
  useFinishSimulation,
  useGetUnifiedSimulatorEndpointFlag
} from '@/modules/simulator/hooks'
import {
  SimulatorContext,
  SimulatorDispatchContext
} from '@/modules/simulator/contexts'
import {
  FormTemplate,
  CustomFormProvider
} from '@/modules/simulator/components/Forms'
import { dispatchEvent } from '@/global/utils'
import {
  ButtonType,
  EventName
} from '@/global/utils/analytics-events/types/common'
import { setSimulationTimestampDataStorage } from '@/modules/simulator/functions/simulationStorage'
import { useDispatchPageEvent, usePreventDoubleClick } from '@/global/hooks'
import {
  CityField,
  CompanyCNPJField,
  PhoneField,
  ResponsibleEmailField,
  ResponsibleNameField
} from '@/modules/simulator/components/Forms/Fields'
import {
  buildPayload,
  dispatchAnalyticsEvents,
  strings
} from './MECompanyForm.helpers'
import { MECompanyFormProps, MEForm } from './MECompanyForm.types'
import { useCreateSimulation } from '@/modules/simulator/hooks/useCreateSimulation'

const MECompanyForm: FunctionComponent<MECompanyFormProps> = ({ onFinish }) => {
  /**
   * Events
   */

  useDispatchPageEvent({
    eventName: EventName.PAGE_VIEWED,
    pageTitle: strings.title
  })

  /**
   * Contexts
   */

  const { simulation } = useContext(SimulatorContext)
  const { stepDispatch, simulationDispatch } = useContext(
    SimulatorDispatchContext
  )

  /**
   * Form
   */

  const methods = useForm<MEForm>({
    resolver: zodResolver(meCompanyFormSchema),
    defaultValues: {
      responsibleName: '',
      responsibleEmail: '',
      telefone: '',
      city: sp,
      cnpj: ''
    }
  })

  const { getValues, watch } = methods

  const formValues = watch()
  const isFormFilled = Object.values(formValues).every(value => value?.trim())

  /**
   * Mutation callbacks
   */

  const onFinishSuccess = () => {
    setSimulationTimestampDataStorage(new Date().toISOString())

    onFinish()
  }

  const onAnswerSuccess = () => {
    const data = getValues()
    const payload = buildPayload(data, simulation.answers)

    simulationDispatch({
      type: 'SET_ANSWER',
      payload
    })

    finishSimulation()
  }

  const onCreateSimulationSuccess = () => {
    const data = getValues()
    const payload = buildPayload(data, simulation.answers)

    simulationDispatch({
      type: 'SET_ANSWER',
      payload
    })

    onFinishSuccess()
  }

  /**
   * Mutations and queries
   */

  const { value: isUnifiedSimulatorEndpointFlag } =
    useGetUnifiedSimulatorEndpointFlag()

  const { mutate: answer, isPending: isAnswerPending } = useAnswerSimulation({
    onSuccess: onAnswerSuccess
  })

  const { isPending: isFinishSimulationPending, mutate: finishSimulation } =
    useFinishSimulation({
      onSuccess: onFinishSuccess
    })

  const { mutate: createSimulation, isPending: isCreateSimulationPending } =
    useCreateSimulation({
      onSuccess: onCreateSimulationSuccess
    })

  /**
   * Handlers
   */

  const onSubmit: SubmitHandler<MEForm> = usePreventDoubleClick(data => {
    dispatchAnalyticsEvents(data)

    const payload = buildPayload(data, simulation.answers)

    if (isUnifiedSimulatorEndpointFlag) {
      createSimulation(payload)
    } else {
      answer({
        id: simulation.id,
        payload: { ...simulation.answers, ...payload }
      })
    }
  })

  const onBack = usePreventDoubleClick(() => {
    simulationDispatch({ type: 'RESET_ME' })
    stepDispatch({ type: 'SET_STEP', payload: 1 })

    dispatchEvent({
      eventName: EventName.BUTTON_CLICKED,
      buttonType: ButtonType.NEW_PAGE,
      buttonName: 'Voltar'
    })
  })

  const loading =
    isAnswerPending || isFinishSimulationPending || isCreateSimulationPending

  return (
    <CustomFormProvider
      formMethods={methods}
      onSubmit={onSubmit}
    >
      <FormTemplate
        title={strings.title}
        submitButtonLabel={strings.submitLabel}
        backButton
        disabled={!isFormFilled}
        loading={loading}
        onBack={onBack}
      >
        <div className='flex flex-col gap-gl-spacing-04'>
          <ResponsibleNameField />

          <PhoneField />

          <ResponsibleEmailField />

          <CityField />

          <CompanyCNPJField />

          <WHelper mode='tertiary'>{lgpdConsentText}</WHelper>
        </div>
      </FormTemplate>
    </CustomFormProvider>
  )
}

export { MECompanyForm }
